/*
 * AutorizacionesRiesgoAlto.jsx — oct-2026
 *
 * Expediente del cliente (Perfil 360 → PLD): historial de las autorizaciones del Oficial
 * de Cumplimiento que liberaron una operación de un cliente en grado de riesgo ALTO.
 * Muestra quién, cuándo, el motivo y la carta PDF (descarga con URL firmada).
 */
import { useState } from "react";
import { ShieldCheck, FileDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function AutorizacionesRiesgoAlto({ autorizaciones }) {
  const [bajando, setBajando] = useState("");
  const rows = autorizaciones || [];

  const abrirCarta = async (docId) => {
    setBajando(docId);
    try {
      const { data } = await api.post(`/documentos/${docId}/signed-url`);
      window.open(`${BACKEND_URL}${data.url}`, "_blank", "noopener");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBajando("");
    }
  };

  return (
    <div data-testid="c360-autorizaciones-riesgo-alto">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Autorizaciones del Oficial de Cumplimiento (riesgo ALTO) — una por solicitud
      </p>
      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-500"
           data-testid="c360-autorizaciones-riesgo-alto-vacio">
          Sin autorizaciones registradas. Si el cliente está en grado ALTO, cada operación
          requiere la autorización del Oficial de Cumplimiento con carta firmada en PDF.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((a) => (
            <div key={a.id} data-testid={`c360-autorizacion-riesgo-alto-${a.id}`}
                 className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-700" />
                <span className="font-medium text-emerald-900">{a.autorizado_por || "—"}</span>
                <span className="text-xs text-slate-600">· {a.fecha || String(a.ts || "").slice(0, 10)}</span>
                <span className="rounded bg-emerald-700 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  Solicitud {String(a.solicitud_id || "").slice(0, 8)}…
                </span>
                {a.grado_riesgo && (
                  <span className="text-[11px] text-slate-500">· grado {a.grado_riesgo}</span>
                )}
                {a.carta_doc_id && (
                  <Button size="sm" variant="outline" className="ml-auto h-7 text-xs"
                    disabled={bajando === a.carta_doc_id}
                    onClick={() => abrirCarta(a.carta_doc_id)}
                    data-testid={`c360-autorizacion-carta-${a.id}`}>
                    {bajando === a.carta_doc_id
                      ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      : <FileDown className="mr-1 h-3 w-3" />}
                    {a.carta_nombre || "Carta PDF"}
                  </Button>
                )}
              </div>
              <p className="mt-1 text-[13px] text-slate-700">
                Motivo: {a.motivo}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
